import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { Mail, Github, Linkedin, ArrowUpRight, ExternalLink } from "lucide-react";
import { projects, experience, stats, fadeUp, scaleIn, ScrollProgress } from "./portfolioData";

// ─────────────────────────────────────────────────────────────────────
// Background
// ─────────────────────────────────────────────────────────────────────

const AuroraBackground = () => {
  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -200]);

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-[#0c0618]" />
      <motion.div style={{ y: y1 }} className="aurora-layer aurora-layer-1" />
      <motion.div style={{ y: y2 }} className="aurora-layer aurora-layer-2" />
      <motion.div style={{ y: y3 }} className="aurora-layer aurora-layer-3" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(12,6,24,0.5)_70%)]" />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Custom Cursor
// ─────────────────────────────────────────────────────────────────────

const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const pos = useRef({ x: 0, y: 0 });
  const ringPos = useRef({ x: 0, y: 0 });
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) {
      setIsTouch(true);
      return;
    }

    const onMouseMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.left = `${e.clientX}px`;
        dotRef.current.style.top = `${e.clientY}px`;
      }
    };

    const onHoverIn = () => {
      dotRef.current?.classList.add("hovering");
      ringRef.current?.classList.add("hovering");
    };
    const onHoverOut = () => {
      dotRef.current?.classList.remove("hovering");
      ringRef.current?.classList.remove("hovering");
    };

    document.addEventListener("mousemove", onMouseMove);
    const interactives = document.querySelectorAll("a, button, .social-icon, .tag-pill");
    interactives.forEach((el) => {
      el.addEventListener("mouseenter", onHoverIn);
      el.addEventListener("mouseleave", onHoverOut);
    });

    let raf;
    const animateRing = () => {
      ringPos.current.x += (pos.current.x - ringPos.current.x) * 0.15;
      ringPos.current.y += (pos.current.y - ringPos.current.y) * 0.15;
      if (ringRef.current) {
        ringRef.current.style.left = `${ringPos.current.x}px`;
        ringRef.current.style.top = `${ringPos.current.y}px`;
      }
      raf = requestAnimationFrame(animateRing);
    };
    raf = requestAnimationFrame(animateRing);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      interactives.forEach((el) => {
        el.removeEventListener("mouseenter", onHoverIn);
        el.removeEventListener("mouseleave", onHoverOut);
      });
      cancelAnimationFrame(raf);
    };
  }, [isTouch]);

  if (isTouch) return null;
  return (
    <>
      <div ref={dotRef} className="cursor-dot" />
      <div ref={ringRef} className="cursor-ring" />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Particle Field
// ─────────────────────────────────────────────────────────────────────

const ParticleField = () => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);

    const PARTICLE_COUNT = 70;
    const CONNECTION_DIST = 120;
    const MOUSE_RADIUS = 150;

    const particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
      });
    }

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    canvas.parentElement.addEventListener("mousemove", onMouseMove);

    const animate = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      for (const p of particles) {
        const dx = p.x - mouse.current.x;
        const dy = p.y - mouse.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < MOUSE_RADIUS && dist > 0) {
          const force = (MOUSE_RADIUS - dist) / MOUSE_RADIUS;
          p.vx += (dx / dist) * force * 0.3;
          p.vy += (dy / dist) * force * 0.3;
        }
        p.vx *= 0.99;
        p.vy *= 0.99;
        p.x = (p.x + p.vx + w) % w;
        p.y = (p.y + p.vy + h) % h;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(139, 92, 246, 0.5)";
        ctx.fill();
      }

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(139, 92, 246, ${0.15 * (1 - dist / CONNECTION_DIST)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resize);
      canvas.parentElement?.removeEventListener("mousemove", onMouseMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="particle-canvas"
      style={{ width: "100%", height: "100%", pointerEvents: "auto" }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────
// Text Effects
// ─────────────────────────────────────────────────────────────────────

const LetterReveal = ({ text, className, delay = 0 }) => (
  <span className={className} aria-label={text}>
    {text.split("").map((char, i) => (
      <motion.span
        key={i}
        initial={{ opacity: 0, filter: "blur(12px)", y: 20 }}
        animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
        transition={{ delay: delay + i * 0.04, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ display: "inline-block", whiteSpace: char === " " ? "pre" : "normal" }}
      >
        {char}
      </motion.span>
    ))}
  </span>
);

const WordReveal = ({ text, className, delay = 0 }) => {
  const words = text.split(" ");
  return (
    <span className={className}>
      {words.map((word, i) => (
        <React.Fragment key={i}>
          <motion.span
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: delay + i * 0.08, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ display: "inline-block" }}
          >
            {word}
          </motion.span>
          {i < words.length - 1 && " "}
        </React.Fragment>
      ))}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Interactive Wrappers
// ─────────────────────────────────────────────────────────────────────

const TiltCard = ({ children, className }) => {
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0, glowX: 50, glowY: 50 });

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTilt({ rotateX: (0.5 - y) * 15, rotateY: (x - 0.5) * 15, glowX: x * 100, glowY: y * 100 });
    e.currentTarget.style.setProperty("--spotlight-x", `${x * 100}%`);
    e.currentTarget.style.setProperty("--spotlight-y", `${y * 100}%`);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ rotateX: 0, rotateY: 0, glowX: 50, glowY: 50 });
  }, []);

  return (
    <div
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(800px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg)`,
        transition: "transform 0.15s ease-out",
        transformStyle: "preserve-3d",
      }}
    >
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-0"
        style={{ background: `radial-gradient(400px circle at ${tilt.glowX}% ${tilt.glowY}%, rgba(139, 92, 246, 0.15), transparent 50%)` }}
      />
      {children}
    </div>
  );
};

const MagneticButton = ({ children, className, ...props }) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    const rect = ref.current.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width / 2);
    const dy = e.clientY - (rect.top + rect.height / 2);
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 100) {
      const pull = (100 - dist) / 100;
      setOffset({ x: dx * pull * 0.3, y: dy * pull * 0.3 });
    }
  }, []);

  const handleMouseLeave = useCallback(() => setOffset({ x: 0, y: 0 }), []);

  return (
    <div
      ref={ref}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ transform: `translate(${offset.x}px, ${offset.y}px)`, transition: "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)" }}
      {...props}
    >
      {children}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Stats / Counter
// ─────────────────────────────────────────────────────────────────────

const AnimatedCounter = ({ value, suffix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / 1500, 1);
      setCount(Math.round((1 - Math.pow(1 - progress, 3)) * value));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isInView, value]);

  return <span ref={ref} className="tabular-nums">{count}{suffix}</span>;
};

const StatsBar = () => (
  <motion.div
    className="stats-bar relative z-10 mx-auto mt-36 max-w-4xl px-6 py-10 rounded-2xl"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7 }}
    viewport={{ once: true }}
  >
    <div className="flex justify-center items-center gap-8 md:gap-16 flex-wrap">
      {stats.map((stat, i) => (
        <div key={i} className="text-center">
          <div className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-indigo-300 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            <AnimatedCounter value={stat.value} suffix={stat.suffix} />
          </div>
          <div className="mt-2 text-sm font-medium text-white/50 uppercase tracking-widest">{stat.label}</div>
        </div>
      ))}
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────
// Section Header
// ─────────────────────────────────────────────────────────────────────

const SectionHeader = ({ children }) => (
  <motion.h2
    className="mb-12 text-center text-3xl font-bold tracking-tight md:text-4xl"
    initial={{ opacity: 0, clipPath: "inset(0 100% 0 0)" }}
    whileInView={{ opacity: 1, clipPath: "inset(0 0% 0 0)" }}
    transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
    viewport={{ once: true }}
  >
    <span className="section-accent">{children}</span>
  </motion.h2>
);

// ─────────────────────────────────────────────────────────────────────
// Cards
// ─────────────────────────────────────────────────────────────────────

const ProjectCard = ({ project, index }) => (
  <motion.div variants={scaleIn} custom={index} initial="hidden" whileInView="show" viewport={{ once: true }}>
    <TiltCard className="card-spotlight gradient-border group relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-md transition-shadow duration-300 hover:shadow-2xl hover:shadow-purple-500/10 animate-breathe">
      <h3 className="relative z-10 flex items-center text-xl font-semibold">
        {project.name}
        {project.url && (
          <ArrowUpRight className="ml-1.5 h-4 w-4 opacity-50 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        )}
      </h3>
      <p className="relative z-10 mt-3 text-sm leading-relaxed text-slate-300/90">{project.description}</p>
      <div className="relative z-10 mt-5 flex flex-wrap gap-2">
        {project.tags.map((tag) => (
          <span key={tag} className="tag-pill">{tag}</span>
        ))}
      </div>
      {project.url && (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <a href={project.url} target="_blank" rel="noreferrer" aria-label={`View ${project.name}`} className="absolute inset-0 z-20 rounded-2xl" />
      )}
    </TiltCard>
  </motion.div>
);

const ExperienceItem = ({ exp, index, isLast }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [showBurst, setShowBurst] = useState(false);

  useEffect(() => {
    if (isInView) setShowBurst(true);
  }, [isInView]);

  return (
    <motion.li ref={ref} className="relative pl-10" variants={fadeUp} custom={index} initial="hidden" whileInView="show" viewport={{ once: true }}>
      <span className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-teal-400 bg-teal-400/30 animate-pulse-dot" />
      {showBurst && <span className="dot-burst" />}
      {!isLast && (
        <span className="absolute left-[7px] top-6 bottom-[-40px] w-[2px] bg-gradient-to-b from-teal-400/40 to-purple-500/10 timeline-line-glow" />
      )}
      <div className="text-lg font-semibold leading-snug">
        {exp.role} <span className="text-purple-300/80 font-medium">· {exp.company}</span>
      </div>
      <div className="mt-1 text-xs font-medium uppercase tracking-widest text-white/50">{exp.period}</div>
      <p className="mt-2.5 text-sm leading-relaxed text-slate-300/90">{exp.description}</p>
    </motion.li>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Loading Curtain
// ─────────────────────────────────────────────────────────────────────

const LoadingCurtain = ({ onComplete }) => (
  <motion.div
    className="loading-curtain"
    initial={{ opacity: 1 }}
    animate={{ opacity: 0 }}
    transition={{ duration: 0.8, delay: 0.3 }}
    onAnimationComplete={onComplete}
    style={{ pointerEvents: "none" }}
  />
);

// ─────────────────────────────────────────────────────────────────────
// Portfolio
// ─────────────────────────────────────────────────────────────────────

const PortfolioExtreme = () => {
  const [curtainDone, setCurtainDone] = useState(false);

  return (
    <div className="noise-overlay relative min-h-screen w-full overflow-x-hidden pb-32 text-white">
      <CustomCursor />
      <AuroraBackground />
      <ScrollProgress />

      <AnimatePresence>
        {!curtainDone && <LoadingCurtain onComplete={() => setCurtainDone(true)} />}
      </AnimatePresence>

      {/* Hero */}
      <header className="relative z-10 mx-auto max-w-5xl px-6 pt-32 md:pt-40 text-center">
        <div className="absolute inset-0 -top-32 md:-top-40 overflow-hidden pointer-events-auto" style={{ zIndex: 0 }}>
          <ParticleField />
        </div>

        <div className="relative z-10">
          <LetterReveal
            text="William Soylemez"
            className="text-6xl font-extrabold tracking-tight md:text-7xl lg:text-8xl bg-gradient-to-r from-indigo-300 via-purple-400 to-pink-400 bg-[length:200%_200%] bg-clip-text text-transparent animate-text-gradient"
            delay={0.5}
          />

          <div className="mx-auto mt-8 max-w-xl text-lg leading-relaxed text-slate-300/90 md:text-xl">
            <WordReveal
              text="CS × Math × Env Studies @ Tufts. Building tools that help people & planet. Full‑stack, cloud computing, networks."
              delay={1.2}
            />
          </div>

          <nav className="mt-10 flex justify-center gap-5">
            {[
              { href: "mailto:william.soylemez@gmail.com", label: "email", Icon: Mail, delay: 1.8 },
              { href: "https://github.com/william-soylemez", label: "github", Icon: Github, delay: 1.95 },
              { href: "https://www.linkedin.com/in/william-soylemez-307709255/", label: "linkedin", Icon: Linkedin, delay: 2.1 },
            ].map(({ href, label, Icon, delay }) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, scale: 0, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <MagneticButton>
                  <a
                    href={href}
                    target={href.startsWith("mailto") ? undefined : "_blank"}
                    rel="noreferrer"
                    aria-label={label}
                    className="social-icon block"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                </MagneticButton>
              </motion.div>
            ))}
          </nav>
        </div>
      </header>

      {/* About */}
      <section className="relative z-10 mx-auto mt-36 max-w-4xl px-6">
        <SectionHeader>About Me</SectionHeader>
        <motion.div
          className="card-spotlight gradient-border rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center text-lg leading-relaxed backdrop-blur-md text-slate-200/90"
          variants={fadeUp}
          custom={1}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          Hi everyone! I'm Will, a rising Senior at Tufts University studying
          Computer Science, Environmental Studies, and Mathematics. I'm
          passionate about using technical skills to solve pressing problems and
          make life better for real people. In my free time, I like to cook,
          read, run, and mix cocktails.
        </motion.div>
      </section>

      {/* Stats */}
      <StatsBar />

      {/* Projects */}
      <section className="relative z-10 mx-auto mt-36 max-w-5xl px-6">
        <SectionHeader>Projects</SectionHeader>
        <div className="grid gap-8 md:grid-cols-2">
          {projects.map((project, i) => (
            <ProjectCard key={i} project={project} index={i + 1} />
          ))}
        </div>
      </section>

      {/* Experience */}
      <section className="relative z-10 mx-auto mt-36 max-w-4xl px-6">
        <SectionHeader>Experience</SectionHeader>
        <ul className="space-y-12">
          {experience.map((exp, i) => (
            <ExperienceItem key={i} exp={exp} index={i + 1} isLast={i === experience.length - 1} />
          ))}
        </ul>
      </section>

      {/* Resume CTA */}
      <motion.div
        className="relative z-10 mx-auto mt-40 max-w-max"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <MagneticButton className="resume-cta animate-bob">
          <span className="orbit-ring" />
          <a
            href="/resume.pdf"
            target="_blank"
            rel="noreferrer"
            className="shimmer-btn animate-glow-pulse group flex items-center gap-2.5 rounded-full px-8 py-4 text-base font-semibold text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/30 hover:scale-105"
          >
            View Résumé
            <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
        </MagneticButton>
      </motion.div>

      {/* Footer */}
      <footer className="relative z-10 mx-auto mt-28 max-w-4xl px-6 text-center">
        <div className="mx-auto mb-6 h-px w-24 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <p className="text-sm text-white/40 tracking-wide">
          &copy; {new Date().getFullYear()} William Soylemez &middot; Built with
          React + Tailwind + Framer&nbsp;Motion
        </p>
      </footer>
    </div>
  );
};

export default PortfolioExtreme;
